import { getLocalTimeZone, now } from "@internationalized/date"
import { queryOptions } from "@tanstack/react-query"
import { fetchAuthSession } from "aws-amplify/auth"

import api from "@/utilities/api"

import {
  GatedCheckoutError,
  GatedConfirmSetupIntentError,
  GatedPayError,
  GatedPaymentIntentError,
  GatedSetupIntentError,
} from "@/utilities/expectedErrors"

import {
  CHECKOUT_BASE_KEY,
  CONFIRM_PAYMENT_BASE_KEY,
  CONFIRM_SETUP_INTENT_BASE_KEY,
  GATED_BASE_KEY,
  PAYMENT_INTENT_BASE_KEY,
  PAYMENT_PROCESSOR_BASE_KEY,
  SETUP_INTENT_BASE_KEY,
} from "./keys"

export const setupIntentQueryOptions = (args: {
  express: boolean | undefined
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      SETUP_INTENT_BASE_KEY,
      args.express,
    ],
    queryFn: async () => {
      const authSession = await fetchAuthSession()
      const token = authSession.tokens?.idToken?.toString()
      const rsp = await api.gated.setupIntent.POST({
        cognitoIDToken: token,
        isRapidCheckout: !!args.express,
      })
      if ("failReason" in rsp) {
        throw new GatedSetupIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const confirmSetupIntentQueryOptions = (args: { sessionID: string }) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      CONFIRM_SETUP_INTENT_BASE_KEY,
      args.sessionID,
    ],
    queryFn: async () => {
      const rsp = await api.gated.confirmSetupIntent.POST({})

      if ("failReason" in rsp) {
        throw new GatedConfirmSetupIntentError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const checkoutQueryOptions = (args: { sessionID: string }) => {
  return queryOptions({
    queryKey: [GATED_BASE_KEY, CHECKOUT_BASE_KEY, args.sessionID],
    queryFn: async () => {
      const rsp = await api.gated.checkout.POST({ sessionID: args.sessionID })

      if ("failReason" in rsp) {
        throw new GatedCheckoutError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    // Note: This causes a double load (cached data + fresh data refetch).
    // This is expected React Query caching behavior. staleTime: Infinity fixes it visually but not sure on the back end leaving it off for the time being
    refetchOnWindowFocus: false,
  })
}

export const paymentIntentQueryOptions = (args: {
  sessionID: string
  pamID: string
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      PAYMENT_INTENT_BASE_KEY,
      args.sessionID,
      args.pamID,
    ],
    queryFn: async () => {
      const authSession = await fetchAuthSession()
      const token = authSession.tokens?.idToken?.toString()
      const rsp = await api.gated.paymentIntent.POST({
        pamID: args.pamID,
        cognitoIDToken: token,
      })
      if ("failReason" in rsp) {
        throw new GatedPaymentIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const confirmPaymentIntentQueryOptions = (args: {
  stripePaymentIntentID: string | null
  pamID: string
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      CONFIRM_PAYMENT_BASE_KEY,
      args,
    ],
    queryFn: async () => {
      const rsp = await api.gated.pay.POST({
        stripePaymentIntentID: args.stripePaymentIntentID,
        termsAndConditionsOK: now(getLocalTimeZone()).toAbsoluteString(),
        pamID: args.pamID,
      })

      if ("failReason" in rsp) {
        throw new GatedPayError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
  })
}
